import { useState, useEffect, useRef } from 'react';
import { PHASE_META } from '../constants/appConstants.js';
import LessonProgressBar from './study/LessonProgressBar.jsx';
// ─────────────────────────────────────────────
// Single question card
// ─────────────────────────────────────────────
function QuestionCard({ question, index, total, onAnswer, phase }) {
  const [selected, setSelected] = useState(null);
  const [revealed, setRevealed] = useState(false);
  const color = PHASE_META[phase]?.color || '#2dd4bf';

  const handleSelect = (letter) => {
    if (revealed) return;
    setSelected(letter);
    setRevealed(true);
    setTimeout(() => onAnswer(letter === question.correct, letter), 900);
  };

  const isCorrect = (letter) => letter === question.correct;

  return (
    <div style={{
      animation: 'fadeUp 0.3s ease both',
    }}>
      {/* Progress indicator */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 16,
      }}>
        <span style={{
          fontSize: 11,
          fontWeight: 700,
          color: color,
          textTransform: 'uppercase',
          letterSpacing: 0.8,
        }}>
          Question {index + 1} of {total}
        </span>
        <div style={{ display: 'flex', gap: 4 }}>
          {Array.from({ length: total }).map((_, i) => (
            <div key={i} style={{
              width: 20, height: 3, borderRadius: 99,
              background: i < index
                ? color
                : i === index
                  ? `${color}66`
                  : 'var(--border-medium)',
              transition: 'background 0.3s',
            }}/>
          ))}
        </div>
      </div>

      {/* Question text */}
      <p style={{
        color: 'var(--text-primary)',
        fontSize: 15,
        lineHeight: 1.65,
        fontWeight: 500,
        marginBottom: 16,
      }}>
        {question.question}
      </p>

      {/* Options */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {question.options.map(opt => {
          const isSelected = selected === opt.letter;
          const correct    = revealed && isCorrect(opt.letter);
          const wrong      = revealed && isSelected && !isCorrect(opt.letter);

          return (
            <button
              key={opt.letter}
              onClick={() => handleSelect(opt.letter)}
              disabled={revealed}
              style={{
                padding: '14px 18px',
                borderRadius: 12,
                border: `1.5px solid ${
                  correct ? 'var(--progress-strong)' :
                  wrong   ? 'var(--progress-weak)' :
                  isSelected ? color :
                  'var(--border-medium)'
                }`,
                background: correct ? 'rgba(52,211,153,0.12)'  :
                            wrong   ? 'rgba(248,113,113,0.12)'  :
                            isSelected ? `${color}15`          :
                            'var(--bg-elevated)',
                color: correct ? 'var(--progress-strong)' :
                       wrong   ? 'var(--progress-weak)' :
                       'var(--text-primary)',
                fontSize: 14,
                fontWeight: 600,
                textAlign: 'left',
                cursor: revealed ? 'default' : 'pointer',
                transition: 'all 0.2s',
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                fontFamily: 'inherit',
              }}
            >
              <span style={{
                width: 24, height: 24,
                borderRadius: '50%',
                background: correct ? 'var(--progress-strong)' :
                             wrong   ? 'var(--progress-weak)' :
                             isSelected ? color :
                             'var(--border-medium)',
                color: 'var(--accent-text)',
                fontSize: 11,
                fontWeight: 800,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                transition: 'all 0.2s',
              }}>
                {correct && revealed ? '✓' : wrong ? '✗' : opt.letter}
              </span>
              {opt.text}
            </button>
          );
        })}
      </div>

      <style>{`
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(10px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}

// ─────────────────────────────────────────────
// Results summary
// ─────────────────────────────────────────────
function ResultsSummary({ score, total, topic, failedConcepts, onSubmit, phase }) {
  const color   = PHASE_META[phase]?.color || '#2dd4bf';
  const passed  = (score / total) >= 0.80;
  const pct     = Math.round((score / total) * 100);

  return (
    <div style={{ textAlign: 'center', padding: '8px 0', animation: 'fadeUp 0.4s ease both' }}>
      {/* Score circle */}
      <div style={{
        width: 80, height: 80,
        borderRadius: '50%',
        background: passed ? 'rgba(52,211,153,0.12)' : 'rgba(248,113,113,0.12)',
        border: `2px solid ${passed ? 'var(--progress-strong)' : 'var(--progress-weak)'}`,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        margin: '0 auto 16px',
      }}>
        <span style={{
          fontSize: 22,
          fontWeight: 900,
          color: passed ? 'var(--progress-strong)' : 'var(--progress-weak)',
          lineHeight: 1,
        }}>
          {pct}%
        </span>
        <span style={{
          fontSize: 10,
          color: 'var(--text-muted)',
          fontWeight: 600,
        }}>
          {score}/{total}
        </span>
      </div>

      {/* Mastery gate — visible on both pass and fail */}
      <p style={{
        fontSize: 11,
        fontWeight: 700,
        color: passed ? 'var(--progress-strong)' : 'var(--progress-weak)',
        textTransform: 'uppercase',
        letterSpacing: 0.7,
        marginBottom: 6,
      }}>
        {passed ? 'Checkpoint Passed!' : 'Gap Detected'}
        {topic ? `: ${topic}` : ''}
      </p>

      <p style={{
        fontSize: 10,
        fontWeight: 600,
        color: 'var(--text-muted)',
        letterSpacing: 0.4,
        marginBottom: 14,
      }}>
        Mastery gate: 80% required to advance
      </p>

      <p style={{
        fontSize: 13,
        color: 'var(--text-muted)',
        marginBottom: failedConcepts?.length && !passed ? 16 : 24,
        lineHeight: 1.5,
      }}>
        {passed
          ? 'Moving to the next section.'
          : 'AdaptOS found weak spots in this checkpoint. Repair the gap, then retry.'}
      </p>

      {/* Weak areas row — only on fail, only when concept data exists */}
      {!passed && failedConcepts?.length > 0 && (
        <p style={{
          fontSize: 12,
          color: 'var(--text-muted)',
          marginBottom: 24,
          lineHeight: 1.5,
        }}>
          <span style={{ fontWeight: 700, color: 'var(--progress-weak)' }}>Weak areas: </span>
          {failedConcepts.slice(0, 3).map((c, i) => (
            <span key={i}>
              {c.length > 60 ? c.slice(0, 60) + '…' : c}
              {i < Math.min(failedConcepts.length, 3) - 1 ? ' · ' : ''}
            </span>
          ))}
        </p>
      )}

      <button
        onClick={onSubmit}
        style={{
          width: '100%',
          padding: '13px 24px',
          borderRadius: 12,
          background: passed
            ? 'linear-gradient(135deg, var(--accent-hover), var(--progress-strong))'
            : `linear-gradient(135deg, ${color}99, ${color})`,
          border: 'none',
          color: 'var(--accent-text)',
          fontSize: 14,
          fontWeight: 700,
          cursor: 'pointer',
          fontFamily: 'inherit',
          letterSpacing: 0.2,
        }}
      >
        {passed ? 'Continue →' : 'Repair This Gap Together →'}
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────
// FULL-SCREEN — dark score panel (right column)
// ─────────────────────────────────────────────
function ScorePanel({ score, answered, total, passed, pct, elapsed, name, done }) {
  const incorrect = answered - score;
  const mm = String(Math.floor(elapsed / 60)).padStart(2, '0');
  const ss = String(elapsed % 60).padStart(2, '0');
  const size = 80, stroke = 7, r = (size - stroke) / 2, circ = 2 * Math.PI * r, off = circ * (1 - pct / 100);
  const row = (color, glyph, label, value) => (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <span style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'rgba(255,255,255,0.75)' }}>
        <span style={{ color, fontWeight: 800 }}>{glyph}</span> {label}
      </span>
      <span style={{ color: '#fff', fontWeight: 700 }}>{value}</span>
    </div>
  );
  return (
    <div style={{ background: 'var(--surface-dark)', borderRadius: 16, padding: '20px 22px', color: '#fff' }}>
      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 10 }}>
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
          <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="rgba(255,255,255,0.12)" strokeWidth={stroke} />
          <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--brand-emerald)" strokeWidth={stroke} strokeDasharray={circ} strokeDashoffset={off} strokeLinecap="round" transform={`rotate(-90 ${size / 2} ${size / 2})`} style={{ transition: 'stroke-dashoffset 0.5s ease' }} />
          <text x="50%" y="50%" textAnchor="middle" dominantBaseline="central" fontSize="18" fontWeight="800" fill="#fff">{pct}%</text>
        </svg>
      </div>
      <p style={{ textAlign: 'center', fontSize: 13, margin: 0, color: 'rgba(255,255,255,0.85)' }}>{score} of {total} Correct</p>
      {done && passed && name && (
        <p style={{ textAlign: 'center', fontSize: 13, color: 'var(--brand-emerald)', margin: '6px 0 0', fontWeight: 600 }}>Good work, {name}!</p>
      )}
      <div style={{ height: 1, background: 'rgba(255,255,255,0.12)', margin: '14px 0' }} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 9, fontSize: 13 }}>
        {row('var(--brand-emerald)', '✓', 'Correct', score)}
        {row('var(--brand-coral)', '✗', 'Incorrect', incorrect)}
        {row('rgba(255,255,255,0.6)', '◷', 'Time Taken', `${mm}:${ss}`)}
      </div>
    </div>
  );
}

// Gap Detected — full-width dark card shown on a sub-80% checkpoint.
function GapDetected({ topic, pct, score, total }) {
  return (
    <div style={{ background: 'var(--surface-dark)', borderRadius: 14, padding: '30px 28px', borderLeft: '3px solid var(--brand-coral)', textAlign: 'center' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
        <span style={{ color: 'var(--brand-coral)' }} aria-hidden>🎯</span>
        <span style={{ color: 'var(--brand-coral)', fontWeight: 800, fontSize: 14, textTransform: 'uppercase', letterSpacing: 0.6 }}>Gap Detected</span>
        {topic && <span style={{ color: '#fff', fontWeight: 700, fontSize: 14 }}>· Topic: {topic}</span>}
      </div>
      <p style={{ color: 'var(--brand-coral)', fontSize: 14, fontWeight: 700, margin: '0 0 8px' }}>Score: {pct}% ({score} of {total} correct)</p>
      <p style={{ color: 'rgba(255,255,255,0.82)', fontSize: 14.5, margin: '0 auto', lineHeight: 1.65, maxWidth: 560 }}>
        You&apos;re close — M[Emory] will strengthen exactly this concept with you before you retake the checkpoint.
      </p>
    </div>
  );
}

// Full-screen checkpoint layout (section 9). Shares all state/handlers with the
// inline card via props; onComplete fires with the identical contract.
function FullScreenQuiz({ quiz, phase, score, currentIdx, answers, done, elapsed, onAnswer, onComplete, name }) {
  const [reviewMissedOnly, setReviewMissedOnly] = useState(false);
  const total    = quiz.questions.length;
  const answered = answers.length;
  const pct      = total > 0 ? Math.round((score / total) * 100) : 0;
  const passed   = total > 0 && (score / total) >= 0.80;

  const reviewRows = quiz.questions
    .map((q, i) => ({ q, correct: answers[i]?.correct, selected: answers[i]?.selected }))
    .filter(r => (reviewMissedOnly ? r.correct === false : true));

  const optText = (q, letter) => q?.options?.find(o => o.letter === letter)?.text || null;
  const humanizeConcept = (s) => (s ? String(s).replace(/[_-]+/g, ' ').trim() : '');

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflowY: 'auto', background: 'var(--bg-primary)' }}>
      <LessonProgressBar sessionPhase="CHECKPOINT" />

      <div style={{ flex: 1, display: 'flex', gap: 20, padding: '8px 24px 20px', flexWrap: 'wrap', alignItems: 'stretch' }}>
        {/* LEFT — question or review */}
        <div style={{ flex: '1 1 60%', minWidth: 320, background: 'var(--bg-card)', border: '1px solid var(--border-soft)', borderRadius: 16, padding: '24px 28px', display: 'flex', flexDirection: 'column' }}>
          {done ? (
            <>
              <p style={{ fontSize: 18, fontWeight: 800, color: 'var(--text-primary)', margin: '0 0 16px' }}>
                {passed ? 'Checkpoint Passed' : 'Checkpoint Review'}
              </p>
              {reviewMissedOnly ? (
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {/* Routed to M[Emory]: why each was missed + the concept it's tracking */}
                  <div style={{ display: 'inline-flex', alignItems: 'center', gap: 7, alignSelf: 'flex-start', background: 'var(--surface-dark)', borderRadius: 99, padding: '5px 12px' }}>
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--brand-emerald)', boxShadow: '0 0 0 3px rgba(5,150,105,0.25)' }} />
                    <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.5, textTransform: 'uppercase', color: 'var(--brand-emerald)' }}>M[Emory]</span>
                    <span style={{ fontSize: 11.5, fontWeight: 600, color: 'rgba(255,255,255,0.8)' }}>why you missed these</span>
                  </div>
                  {reviewRows.map((r, i) => {
                    const concept = humanizeConcept(r.q?.concept);
                    return (
                      <div key={i} style={{ borderRadius: 12, border: '1px solid rgba(220,38,38,0.30)', background: 'rgba(220,38,38,0.05)', padding: '14px 16px' }}>
                        <p style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)', margin: 0, lineHeight: 1.5 }}>{r.q?.question}</p>
                        <div style={{ marginTop: 9, display: 'flex', flexDirection: 'column', gap: 4 }}>
                          <p style={{ fontSize: 12.5, margin: 0, color: 'var(--text-secondary)' }}>
                            <span style={{ color: 'var(--brand-coral)', fontWeight: 700 }}>Your answer:</span> {optText(r.q, r.selected) || '—'}
                          </p>
                          <p style={{ fontSize: 12.5, margin: 0, color: 'var(--text-secondary)' }}>
                            <span style={{ color: 'var(--brand-emerald)', fontWeight: 700 }}>Correct answer:</span> {optText(r.q, r.q?.correct) || '—'}
                          </p>
                        </div>
                        {concept && (
                          <p style={{ fontSize: 12, margin: '9px 0 0', color: 'var(--text-muted)', lineHeight: 1.5 }}>
                            M[Emory] is tracking this as a <strong style={{ color: 'var(--text-secondary)' }}>{concept}</strong> gap — the repair targets exactly this.
                          </p>
                        )}
                      </div>
                    );
                  })}
                  {reviewRows.length === 0 && (
                    <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: 0 }}>No missed questions — clean run.</p>
                  )}
                </div>
              ) : (
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {reviewRows.map((r, i) => (
                    <div key={i} style={{ flex: 1, display: 'flex', gap: 12, alignItems: 'center', padding: '12px 18px', borderRadius: 12, border: `1px solid var(--border-soft)`, background: r.correct ? 'rgba(5,150,105,0.06)' : 'rgba(220,38,38,0.06)' }}>
                      <span style={{ color: r.correct ? 'var(--brand-emerald)' : 'var(--brand-coral)', fontWeight: 800, fontSize: 15, flexShrink: 0 }}>{r.correct ? '✓' : '✗'}</span>
                      <span style={{ fontSize: 14, color: 'var(--text-primary)', lineHeight: 1.5 }}>{r.q?.question}</span>
                    </div>
                  ))}
                  {reviewRows.length === 0 && (
                    <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: 0 }}>No missed questions — clean run.</p>
                  )}
                </div>
              )}
            </>
          ) : (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <QuestionCard
                key={currentIdx}
                question={quiz.questions[currentIdx]}
                index={currentIdx}
                total={total}
                onAnswer={onAnswer}
                phase={phase}
              />
            </div>
          )}
        </div>

        {/* RIGHT — score panel */}
        <div style={{ flex: '1 1 30%', minWidth: 240, alignSelf: 'flex-start' }}>
          <ScorePanel score={score} answered={answered} total={total} passed={passed} pct={pct} elapsed={elapsed} name={name} done={done} />
        </div>
      </div>

      {/* Done — Gap Detected + actions */}
      {done && (
        <div style={{ padding: '0 24px 24px', display: 'flex', flexDirection: 'column', gap: 12 }}>
          {!passed && <GapDetected topic={quiz.topic || null} pct={pct} score={score} total={total} />}
          <button
            onClick={() => onComplete(score, total, answers)}
            style={{ width: '100%', padding: '13px', borderRadius: 12, border: 'none', background: 'var(--brand-amber)', color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}
          >
            {passed ? 'Continue →' : 'Repair This Together →'}
          </button>
          {!passed && (
            <button
              onClick={() => setReviewMissedOnly(v => !v)}
              style={{ width: '100%', padding: '12px', borderRadius: 12, background: 'var(--bg-card)', border: '1px solid var(--border-medium)', color: 'var(--text-primary)', fontSize: 14, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}
            >
              {reviewMissedOnly ? 'Show All Questions' : 'Review Missed Questions'}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
// Main QuizPanel
// ─────────────────────────────────────────────
export default function QuizPanel({ quiz, onComplete, phase, fullScreen = false, name }) {
  const [currentIdx, setCurrentIdx] = useState(0);
  const [answers,    setAnswers]    = useState([]);  // array of { correct, concept, selected }
  const [done,       setDone]       = useState(false);
  const startRef = useRef(Date.now());
  const [elapsed, setElapsed] = useState(0);

  // Running timer for the full-screen score panel. Stops when done.
  useEffect(() => {
    if (!fullScreen || done) return undefined;
    const t = setInterval(() => setElapsed(Math.floor((Date.now() - startRef.current) / 1000)), 1000);
    return () => clearInterval(t);
  }, [fullScreen, done]);

  if (!quiz?.questions?.length) return null;

  const handleAnswer = (correct, selected) => {
    const currentQ = quiz.questions[currentIdx];
    const concept  = currentQ?.concept || currentQ?.question || null;
    const updated  = [...answers, { correct, concept, selected }];
    setAnswers(updated);

    if (currentIdx + 1 < quiz.questions.length) {
      setCurrentIdx(i => i + 1);
    } else {
      setDone(true);
    }
  };

  const score = answers.filter(a => a.correct).length;

  // Full-screen checkpoint — same state/handlers, identical onComplete contract.
  if (fullScreen) {
    return (
      <FullScreenQuiz
        quiz={quiz}
        phase={phase}
        score={score}
        currentIdx={currentIdx}
        answers={answers}
        done={done}
        elapsed={elapsed}
        onAnswer={handleAnswer}
        onComplete={onComplete}
        name={name}
      />
    );
  }

  return (
    <div style={{
      background: 'var(--bg-elevated)',
      border: '1px solid var(--border-soft)',
      borderRadius: 16,
      padding: '20px 24px',
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        marginBottom: 20,
        paddingBottom: 16,
        borderBottom: '1px solid var(--border-soft)',
      }}>
        <div style={{
          width: 8, height: 8, borderRadius: '50%',
          background: PHASE_META[phase]?.color || '#2dd4bf',
          boxShadow: `0 0 8px ${PHASE_META[phase]?.color || '#2dd4bf'}`,
        }}/>
        <span style={{
          fontSize: 11,
          fontWeight: 700,
          color: 'var(--text-muted)',
          textTransform: 'uppercase',
          letterSpacing: 0.8,
        }}>
          Checkpoint — {quiz.topic}
        </span>
      </div>

      {done ? (
        <ResultsSummary
          score={score}
          total={quiz.questions.length}
          topic={quiz.topic || null}
          failedConcepts={answers.filter(a => !a.correct).map(a => a.concept).filter(Boolean)}
          phase={phase}
          onSubmit={() => onComplete(score, quiz.questions.length, answers)}
        />
      ) : (
        <QuestionCard
          key={currentIdx}
          question={quiz.questions[currentIdx]}
          index={currentIdx}
          total={quiz.questions.length}
          onAnswer={handleAnswer}
          phase={phase}
        />
      )}
    </div>
  );
}
